import React, { useState } from "react";
import { UserVideoWord, UserWordStatus } from "@shared_types";

interface ReviewWordHighlighterProps {
  word: UserVideoWord;
  position: number;
  highlightPosition: number;
  onWordClick: (word: UserVideoWord) => void;
  onDoubleWordClick?: (word: UserVideoWord) => void;
  reviewWord?: UserVideoWord;
}

const ReviewWordHighlighter: React.FC<ReviewWordHighlighterProps> = ({
  word,
  position,
  highlightPosition,
  onWordClick,
  onDoubleWordClick,
  reviewWord,
}) => {
  const highlightColorBlue = "bg-[var(--color-blue)]";
  const reviewWordColor = "bg-[var(--color-yellow)]";
  const learningColorLightYellow = "bg-[var(--color-light-yellow)]";
  const [lastTap, setLastTap] = useState(0);

  const wordCls = (status: UserWordStatus) => {
    if (reviewWord) {
      return ""; // No status-based coloring if reviewWord is provided
    }
    switch (status) {
      case "known":
        return "bg-transparent";
      case "learning":
        return learningColorLightYellow;
      case "new":
        return reviewWordColor;
      default:
        return "bg-transparent";
    }
  };

  const handleClick = () => {
    if (reviewWord) {
      if (word.wordKey === reviewWord.wordKey) return;
    }

    const currentTime = new Date().getTime();
    const tapGap = currentTime - lastTap;
    if (tapGap < 300 && tapGap > 0 && onDoubleWordClick) {
      onDoubleWordClick(word);
    } else {
      onWordClick(word);
    }
    setLastTap(currentTime);
  };

  return (
    <span
      className="cursor-pointer selection:text-white"
      key={word.videoWordId}
    >
      <span
        data-pos={position}
        className={
          (word.wordKey === reviewWord?.wordKey
            ? reviewWordColor
            : position === highlightPosition
            ? highlightColorBlue + " text-white"
            : wordCls(word.status)) + " rounded px-0.5"
        }
        onClick={handleClick}
      >
        {word.displayText}
      </span>{" "}
    </span>
  );
};

interface HighlightedWordsProps {
  words: UserVideoWord[];
  highlightPosition: number;
  onWordClick: (word: UserVideoWord) => void;
  onDoubleWordClick?: (word: UserVideoWord) => void;
  reviewWord?: UserVideoWord;
}

const ReviewHighlightedWords: React.FC<HighlightedWordsProps> = ({
  words,
  highlightPosition,
  onWordClick,
  onDoubleWordClick,
  reviewWord,
}) => {
  return (
    <p>
      {words.map((word, index) => (
        <ReviewWordHighlighter
          key={word.videoWordId}
          word={word}
          position={index}
          highlightPosition={highlightPosition}
          onWordClick={onWordClick}
          onDoubleWordClick={onDoubleWordClick}
          reviewWord={reviewWord}
        />
      ))}
    </p>
  );
};

export default ReviewHighlightedWords;
